import { logger } from "@dransay/logger";
import * as Sentry from "@sentry/react-router";
import { TRPCError } from "@trpc/server";
import { getHTTPStatusCodeFromError } from "@trpc/server/http";
import { data } from "react-router";

export const UNEXPECTED_ERROR_MESSAGE =
	"An unexpected error occurred. Please try again later.";

/**
 * Error response type for route loaders/actions
 * This structure is recognized by ErrorBoundary for proper display
 */
export interface RouteErrorResponse {
	error: {
		message: string;
		code?: string;
	};
}

/**
 * Converts an error to a proper route error response that React Router won't sanitize.
 *
 * React Router sanitizes thrown errors in production to "Unexpected Server Error"
 * to prevent leaking internal details. This function converts errors to proper
 * `data()` responses with appropriate HTTP status codes.
 *
 * @param error - The error to convert
 * @param context - Optional context for Sentry logging. Pass a stable `route`
 *   identifier (e.g. `"checkout.summary"`) so Sentry fingerprints group by
 *   `(statusCode, route)` instead of collapsing onto the shared minified
 *   throw frame.
 * @param headers - Optional headers for the thrown response. Pass the route
 *   guard's headers so a Set-Cookie from a token refresh is not lost when
 *   the loader fails after the guard already rotated the session.
 * @returns A `data()` response that React Router will preserve
 *
 * @example
 * ```ts
 * export const loader = async ({ request }: LoaderArgs) => {
 *   try {
 *     const result = await caller.customer.getCurrentCustomer();
 *     return data({ customer: result });
 *   } catch (error) {
 *     throwRouteError(error, {
 *       path: new URL(request.url).pathname,
 *       route: "checkout.summary",
 *     });
 *   }
 * };
 * ```
 */
export function throwRouteError(
	error: unknown,
	context?: Record<string, unknown>,
	headers?: HeadersInit,
): never {
	const statusCode =
		error instanceof TRPCError ? getHTTPStatusCodeFromError(error) : 500;
	const route = typeof context?.route === "string" ? context.route : "unknown";

	// Fingerprint by (statusCode, route) so 401/404/503 slices don't collapse
	// onto the same Sentry issue via the shared minified throw frame.
	Sentry.captureException(error, {
		level: "error",
		fingerprint: ["route-error", String(statusCode), route],
		extra: {
			...context,
			errorType: error instanceof TRPCError ? "TRPCError" : "Error",
			errorCode: error instanceof TRPCError ? error.code : undefined,
		},
	});

	if (error instanceof TRPCError) {
		const message = getUserFriendlyMessage(error.code, error.message);

		logger.error(
			{
				error,
				statusCode,
				code: error.code,
				message,
				...context,
			},
			"Route error: TRPCError",
		);

		throw data<RouteErrorResponse>(
			{
				error: {
					message,
					code: error.code,
				},
			},
			{ status: statusCode, headers },
		);
	}

	// For non-TRPC errors, use 500 status
	const message =
		error instanceof Error ? error.message : "An unexpected error occurred";

	logger.error(
		{
			error,
			message,
			...context,
		},
		"Route error: Unknown error",
	);

	throw data<RouteErrorResponse>(
		{
			error: {
				message,
			},
		},
		{ status: 500, headers },
	);
}

/**
 * Maps tRPC error codes to user-friendly messages.
 * Returns the original message for codes that are already user-friendly.
 */
function getUserFriendlyMessage(
	code: TRPCError["code"],
	originalMessage: string,
): string {
	// These error codes typically have user-friendly messages already
	const passthrough: Array<TRPCError["code"]> = [
		"BAD_REQUEST",
		"NOT_FOUND",
		"FORBIDDEN",
		"CONFLICT",
		"PRECONDITION_FAILED",
		"PAYLOAD_TOO_LARGE",
		"UNPROCESSABLE_CONTENT",
		"TOO_MANY_REQUESTS",
	];

	if (passthrough.includes(code)) {
		return originalMessage;
	}

	// Map internal error codes to user-friendly messages
	switch (code) {
		case "UNAUTHORIZED":
			return "You must be logged in to access this page.";
		case "TIMEOUT":
			return "The request took too long. Please try again.";
		case "INTERNAL_SERVER_ERROR":
			return UNEXPECTED_ERROR_MESSAGE;
		case "METHOD_NOT_SUPPORTED":
		case "NOT_IMPLEMENTED":
			return "This feature is not available.";
		case "CLIENT_CLOSED_REQUEST":
			return "The request was cancelled.";
		default:
			// For unknown codes, use a generic message but log the original
			logger.warn(
				{ code, originalMessage },
				"Unknown tRPC error code, using generic message",
			);
			return "An error occurred. Please try again.";
	}
}

/**
 * Type guard to check if a value is a RouteErrorResponse
 */
export function isRouteErrorResponse(
	value: unknown,
): value is RouteErrorResponse {
	return (
		typeof value === "object" &&
		value !== null &&
		"error" in value &&
		typeof (value as RouteErrorResponse).error === "object" &&
		"message" in (value as RouteErrorResponse).error
	);
}
